import { HTMLTable } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';
import { alphaSort } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { Link } from 'src/components/Link';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerTableLinkTestId,
  getProjectDiagramEntityDrawerTableRowTestId,
} from 'src/constants/testIds';
import * as copy from 'src/templates/copy';

type ProjectDiagramRelationshipDrawerTableProps = {
  domains: SelectItem[];
  highlightedDomains?: Set<string>;
  highlightedRanges?: Set<string>;
  ranges: SelectItem[];
  onClick?: (item: SelectItem) => void;
};

const ProjectDiagramRelationshipDrawerTableBody: React.VFC<
  ProjectDiagramRelationshipDrawerTableProps
> = ({
  domains,
  highlightedDomains = new Set(),
  highlightedRanges = new Set(),
  ranges,
  onClick,
}) => {
  const { highlightedIndices, sortedRows } = React.useMemo(() => {
    const sortedDomains = domains.sort(alphaSort(({ text }) => text));
    const sortedRanges = ranges.sort(alphaSort(({ text }) => text));
    const highlightedIndices = new Set<number>();
    let rowIdx = 0;
    const sortedRows = sortedDomains
      .map((domain) =>
        sortedRanges.map((range) => {
          if (
            highlightedDomains.has(domain.value) &&
            highlightedRanges.has(range.value)
          ) {
            highlightedIndices.add(rowIdx);
          }
          rowIdx += 1;
          return {
            domain,
            range,
          };
        })
      )
      .flat();

    return { highlightedIndices, sortedRows };
  }, [domains, highlightedDomains, highlightedRanges, ranges]);
  return (
    <tbody>
      {sortedRows.map((row, idx) => (
        <tr
          key={`${row.domain.value}-${row.range.value}`}
          className={classNames({
            'sd-highlighted-table-row': highlightedIndices.has(idx),
            'sd-highlighted-table-row__no-top-border': highlightedIndices.has(
              idx - 1
            ),
          })}
          data-testid={getProjectDiagramEntityDrawerTableRowTestId(
            row.domain.value,
            PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
            idx
          )}
        >
          <td>
            {onClick ? (
              <Link
                onClick={() => onClick(row.domain)}
                testId={getProjectDiagramEntityDrawerTableLinkTestId(
                  row.domain.value,
                  PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
                  idx
                )}
              >
                <Text ellipsize>{row.domain.text}</Text>
              </Link>
            ) : (
              <Text ellipsize>{row.domain.text}</Text>
            )}
          </td>
          <td>
            {onClick ? (
              <Link
                onClick={() => onClick(row.range)}
                testId={getProjectDiagramEntityDrawerTableLinkTestId(
                  row.range.value,
                  PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
                  idx
                )}
              >
                <Text ellipsize>{row.range.text}</Text>
              </Link>
            ) : (
              <Text ellipsize>{row.range.text}</Text>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  );
};

export const ProjectDiagramRelationshipDrawerTable: React.VFC<
  ProjectDiagramRelationshipDrawerTableProps
> = ({
  domains,
  highlightedDomains = new Set(),
  highlightedRanges = new Set(),
  ranges,
  onClick,
}) =>
  domains.length && ranges.length ? (
    <HTMLTable
      className={classNames(
        'sd-drawer-project-diagram-entity-table',
        'sd-drawer-project-diagram-entity-table--relationship'
      )}
      data-testid={TEST_IDS.projectDiagram.entityDrawer.relationshipTable}
    >
      <thead>
        <tr>
          <th>{copy.components.projectDiagram.relationshipDrawer.domain}</th>
          <th>{copy.components.projectDiagram.relationshipDrawer.range}</th>
        </tr>
      </thead>

      <ProjectDiagramRelationshipDrawerTableBody
        domains={domains}
        highlightedDomains={highlightedDomains}
        highlightedRanges={highlightedRanges}
        onClick={onClick}
        ranges={ranges}
      />
    </HTMLTable>
  ) : null;
